import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip } from '@ui';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Imported straight from the module instead of the barrel: the barrel re-exports this component,
// so going through it would create an import cycle.
import { descriptionClassName, FormSelect, labelClassNames, Option } from './FormSelect';
import { toSourceList } from './formMappingValue';

interface Props {
    /** Form path of this mapping entry, e.g. "form_mapping.name". */
    name: string;
    /** Label of the target field this row maps onto. */
    label?: string;
    description?: string;
    /** Selectable form fields, already including the "no mapping" entry. */
    options: Option[];
    /** Allows combining several form fields into this one target. */
    multiple?: boolean;
    /** Upper bound on how many form fields may feed this target. */
    maxSources?: number;
    /**
     * What several fields on this target mean, when it is not "joined into one
     * value with a space between". A target that reads each of its fields on
     * its own — one tick box per group size — is doing something the default
     * sentence describes wrongly, and a wrong explanation of a mapping is worse
     * than none: it is the sentence somebody checks their mapping against.
     */
    joinHint?: string;
    /**
     * What each selectable field turns into, keyed by its path.
     *
     * For the one target whose rows cannot be checked by reading them: four
     * selects holding "4er Gruppe 65,- inkl. Halle (monatlich)" and its
     * siblings say nothing about whether that was understood as a four or as a
     * sixty-five. `note` is printed after the select; `rank` orders the rows.
     *
     * Ordering is presentational and deliberately does not touch the stored
     * value. For a target read field by field the stored order carries no
     * meaning at all, so sorting it would be writing a value to express
     * something the value does not say — and this component must never write
     * one (see the prefill effect below).
     */
    slotAnnotations?: Record<string, { note: string; rank: number }>;
}

/**
 * One row of a form mapping: the target field on the left, the form field(s) feeding it on the right.
 *
 * With a single source this renders exactly the select it always rendered and stores a plain string,
 * so nothing changes for the mappings and consumers that only ever need 1:1. Only once the user adds
 * a second field does the value become a list of paths, which the backend combines into one value.
 */
export const FormMappingRow = ({
    name,
    label,
    description,
    options,
    multiple = false,
    maxSources = 5,
    joinHint,
    slotAnnotations
}: Props) => {
    const { t } = useTranslation('form');
    const { setValue, getValues } = useFormContext();
    const value = useWatch({ name });

    // The paths currently stored, empty slots included: an added-but-not-yet-filled select still
    // needs a slot, which toSourceList() would drop.
    const slots = useMemo<string[]>(() => {
        if (Array.isArray(value)) return value.map((path) => (typeof path === 'string' ? path : ''));

        return [typeof value === 'string' ? value : ''];
    }, [value]);

    const filledCount = toSourceList(value).length;
    const [slotCount, setSlotCount] = useState(() => Math.max(filledCount, 1));

    // A prefill arriving after mount (backend guess, stored settings) can carry more fields than the
    // row currently shows. Grow to fit it, and collapse back once the mapping is cleared entirely —
    // never write a value here, or an auto-saving settings form would rewrite untouched mappings.
    useEffect(() => {
        setSlotCount((current) => (filledCount === 0 ? 1 : Math.max(current, filledCount)));
    }, [filledCount]);

    /** Appends an empty slot, turning a single string value into a list on the way. */
    const addSource = () => {
        const current = getValues(name);
        const paths = Array.isArray(current) ? current : [typeof current === 'string' ? current : ''];

        setValue(name, [...paths, ''], { shouldDirty: true });
        setSlotCount(paths.length + 1);
    };

    /**
     * The slot indices in the order they are shown, which is not their order in
     * the value.
     *
     * A club builds its tick boxes in whatever order it likes — the one real
     * form this was measured against runs 4er, 3er, 2er, 1er — and the mapping
     * inherits that order because it collects fields as the form lists them.
     * Read top to bottom it looks like a list somebody forgot to sort, which is
     * exactly how it was reported.
     *
     * Sorted here and only here, so the bindings stay on their own indices: the
     * select at `name.3` remains `name.3` however high up it is drawn, and
     * removing it removes the third entry. Rows with no rank keep their place at
     * the end, in the order they were in — a freshly added empty slot must not
     * jump to the front.
     */
    const order = useMemo(() => {
        const indices = Array.from({ length: slotCount }, (_, index) => index);

        if (!slotAnnotations) return indices;

        return indices.sort((first, second) => {
            const rank = (index: number) => slotAnnotations[slots[index]]?.rank ?? Number.MAX_SAFE_INTEGER;

            return rank(first) - rank(second) || first - second;
        });
    }, [slotAnnotations, slotCount, slots]);

    /** Drops one slot and collapses back to a plain string once a single source is left. */
    const removeSource = (index: number) => {
        const remaining = slots.filter((_, slot) => slot !== index);

        setValue(name, remaining.length > 1 ? remaining : (remaining[0] ?? ''), { shouldDirty: true });
        setSlotCount(Math.max(remaining.length, 1));
    };

    // Single source: identical to the plain select this component replaced, string value included.
    if (slotCount <= 1) {
        return (
            <>
                <FormSelect
                    disableCustomValue
                    name={name}
                    label={label}
                    description={description}
                    options={options}
                />
                {multiple && <AddSourceButton label={t('mapping.addSource')} onPress={addSource} />}
            </>
        );
    }

    return (
        <div className="tw-my-3">
            {label && <label className={labelClassNames()}>{label}</label>}
            {/* Keyed on the slot count so the selects re-register when the bound paths change arity. */}
            <div key={slotCount}>
                {order.map((index) => (
                    <div key={index} className="tw-flex tw-items-center tw-gap-2">
                        {/* Ahead of the select, not behind it.
                            This is the answer the row produces and it was the
                            smallest thing in it — a "→ Einzel" wedged between
                            the select and the delete button, in the position
                            where a stray annotation goes. In front, in a column
                            of its own, the rows read as "Einzel: dieses Feld",
                            the sorted sizes stack into something scannable, and
                            the arrow is unnecessary because the position says
                            what it said.

                            Not a label above the select, which is the other
                            obvious place: this text is derived from the value
                            underneath it and changes when that value changes.
                            A label that rewrites itself when you pick a
                            different option is not a label — it is a result
                            standing in the label's chair.

                            Right-aligned so "Einzel" and a bare "3" both end
                            flush against the select they belong to, and fixed
                            width so a row nothing was recognised in shows as a
                            gap in a straight edge rather than as a shift. */}
                        {slotAnnotations && (
                            <span className="tw-w-14 tw-flex-none tw-truncate tw-text-right tw-text-xs tw-font-semibold tw-tabular-nums tw-text-accents-9">
                                {slotAnnotations[slots[index]]?.note ?? ''}
                            </span>
                        )}
                        <div className="tw-min-w-0 tw-flex-1">
                            <FormSelect disableCustomValue name={`${name}.${index}`} options={options} />
                        </div>
                        {/* Same variant the select's own trigger uses (see DropdownMenu), so both
                            controls end up exactly the same height. */}
                        <Tooltip flat withArrow placement="top" tooltip={t('mapping.removeSource')}>
                            <Button
                                icon={<FontAwesomeIcon icon={faXmark} />}
                                size="sm"
                                color="light"
                                flat
                                aria-label={t('mapping.removeSource')}
                                onPress={() => removeSource(index)}
                            />
                        </Tooltip>
                    </div>
                ))}
            </div>
            <p className={descriptionClassName}>
                {description ? `${description} ` : ''}
                {joinHint ?? t('mapping.joinHint')}
            </p>
            {slotCount < maxSources && <AddSourceButton label={t('mapping.addSource')} onPress={addSource} />}
        </div>
    );
};

/** Small inline affordance that appends another form field to the row. */
const AddSourceButton = ({ label, onPress }: { label: string; onPress: () => void }) => (
    <Button
        icon={<FontAwesomeIcon icon={faPlus} />}
        size="xs"
        color="light"
        flat
        className="tw-mb-2 tw-ml-1"
        onPress={onPress}
    >
        {label}
    </Button>
);
